import axios, { CancelTokenSource } from 'axios';

import { CreateCampaign } from '~pages/CampaignManagement/domain';
import { APIError, UnsupportedStructureError } from '~services/Errors';

import { CampaignFilterType, CampaignResponse, CampaignsResponseDecoder } from './domain';

export const getCampaignsByNextUrl = async (
  nextUrl: string,
  cancelToken?: CancelTokenSource,
): Promise<CampaignResponse | undefined> => {
  const path = nextUrl;
  let resp;

  try {
    resp = await axios.request({
      url: path,
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      cancelToken: cancelToken ? cancelToken.token : undefined,
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return undefined;
    }

    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, (e.response!.data as { error: string }).error || e.message);
    }

    throw new APIError(-1, e as string);
  }

  const decoded = CampaignsResponseDecoder.run(resp.data);

  if (decoded.ok === false) {
    const err = new UnsupportedStructureError(decoded.error.message);

    console.error(decoded.error);
    console.error(err);
    throw err;
  }

  return decoded.result;
};

export const getCampaigns = async ({
  search,
  filters,
  campaignIds,
  accessFilterId,
  cancelToken,
}: {
  search?: string;
  filters?: CampaignFilterType[];
  campaignIds?: number[];
  accessFilterId?: number;
  cancelToken?: CancelTokenSource;
} = {}): Promise<CampaignResponse | undefined> => {
  const path = '/api/internal/campaign/';
  let resp;
  try {
    resp = await axios.request({
      url: path,
      method: 'GET',
      params: {
        search: search?.toLowerCase() || undefined,
        filters,
        campaign_ids: campaignIds,
        access_filter_id: accessFilterId,
      },
      paramsSerializer: {
        indexes: null,
      },
      headers: {
        Accept: 'application/json',
      },
      cancelToken: cancelToken?.token,
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return undefined;
    }

    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, (e.response!.data as { error: string }).error || e.message);
    }

    throw new APIError(-1, e as string);
  }

  const decoded = CampaignsResponseDecoder.run(resp.data);

  if (decoded.ok === false) {
    const err = new UnsupportedStructureError(decoded.error.message);

    console.error(decoded.error);
    console.error(err);
    throw err;
  }

  return decoded.result;
};

export const createCampaign = async (data: CreateCampaign): Promise<void> => {
  const path = '/api/campaign/';

  let body: any = {
    name: data.name,
    description: data.description,
    access_filter_ids: data.accessFilterIds,
    sip_source_phone_number: data.sipSourcePhoneNumber,
    predictive_settings: {
      connect_source_phone_number: data.predictiveSettings?.connectSourcePhoneNumber || '',
    },
  };

  try {
    await axios.request({
      method: 'POST',
      url: path,
      data: body,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, (e.response!.data as { error: string }).error || e.message);
    }

    throw new APIError(-1, e as string);
  }
};
